import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useGetUser } from "../../api/queries/useGetUser";
import { useAppContext } from "../../Context/AppContext";
import {
  earlyStageAgeVerificationSchema,
  parseIsoDateString,
  type EarlyStageAgeVerificationFormValues,
} from "./schemas";
import {
  submitEarlyStageVerification,
  type EarlyStageVerificationStatus,
} from "../../api/earlyStageMembershipApi";
import {
  EARLY_STAGE_MEMBERSHIP,
  isUnder23,
  toDateOnlyString,
} from "../../utils/membershipTier";
import axios from "axios";
import { formatDateForInput } from "../../utils/formatDateForInput";
import "bootstrap-icons/font/bootstrap-icons.css";

const MAX_ID_FILE_SIZE = 5 * 1024 * 1024;
const VALID_ID_TYPES = [
  "image/png",
  "image/jpeg",
  "image/heif",
  "image/heic",
  "application/pdf",
];

interface RegistrationEarlyStageAgeStepProps {
  prefillFromPersonalInfo?: {
    phoneNumber: string;
    city: string;
    country?: string;
  };
  membershipName?: string;
  membershipId?: number;
  isAnnualMembership?: number;
  explorerSegment?: string;
  onAdvanceAfterVerification: (status: EarlyStageVerificationStatus) => void;
  onBack?: () => void;
  onStatusUpdated?: (status: EarlyStageVerificationStatus) => void;
}

function RequirementItem({ children }: { children: ReactNode }) {
  return (
    <li className="d-flex align-items-start gap-2 small text-secondary">
      <i className="bi bi-check-circle-fill text-success flex-shrink-0 mt-1" aria-hidden />
      <span>{children}</span>
    </li>
  );
}

function RegistrationEarlyStageAgeStepInner({
  prefillFromPersonalInfo,
  membershipName = EARLY_STAGE_MEMBERSHIP,
  membershipId,
  isAnnualMembership = 0,
  explorerSegment: _explorerSegment,
  onAdvanceAfterVerification,
  onStatusUpdated,
}: RegistrationEarlyStageAgeStepProps) {
  const { objectId } = useAppContext();
  const queryClient = useQueryClient();
  const { data: userData, isLoading: userLoading } = useGetUser({
    id: objectId || "",
  });
  const idFileInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const [idDocumentFile, setIdDocumentFile] = useState<File | null>(null);
  const [idUploadInvalid, setIdUploadInvalid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const existingIdOnFile = Boolean(userData?.user?.idDocument?.trim());

  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<EarlyStageAgeVerificationFormValues>({
    resolver: yupResolver(earlyStageAgeVerificationSchema),
    defaultValues: { dateOfBirth: "", youngProfessionalConsent: 0 },
  });

  useEffect(() => {
    const u = userData?.user;
    if (!u) return;
    const dobRaw = u.dateOfBirth?.trim();

    reset({
      dateOfBirth: dobRaw ? formatDateForInput(dobRaw) : "",
      youngProfessionalConsent: 0,
    });
  }, [userData, reset]);

  const onSubmit = useCallback(
    async (data: EarlyStageAgeVerificationFormValues) => {
      const dateOfBirth = parseIsoDateString(data.dateOfBirth);
      if (!dateOfBirth) return;

      if (!isUnder23(dateOfBirth)) {
        setError("dateOfBirth", {
          type: "manual",
          message:
            "You must be between 18 and 23 years old to qualify for the Young Professional rate.",
        });
        return;
      }

      if (!idDocumentFile && !existingIdOnFile) {
        setIdUploadInvalid(true);
        return;
      }
      setIdUploadInvalid(false);

      if (!objectId?.trim()) {
        toast.error("Account not found. Please sign in again.");
        return;
      }

      const phoneNumber =
        userData?.user?.phoneNumber?.trim() ||
        prefillFromPersonalInfo?.phoneNumber?.trim() ||
        "";
      const city =
        userData?.user?.city?.trim() ||
        prefillFromPersonalInfo?.city?.trim() ||
        "";
      const country =
        userData?.user?.country?.trim() ||
        prefillFromPersonalInfo?.country?.trim() ||
        "";

      setSubmitting(true);
      try {
        const status = await submitEarlyStageVerification({
          ageTier: "Under23",
          dateOfBirth: toDateOnlyString(dateOfBirth),
          idDocument: idDocumentFile ?? null,
          phoneNumber,
          city,
          country,
          proceedAtStandardRate: false,
        });

        await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
        onStatusUpdated?.(status);

        if (status.requiresAdminApproval) {
          toast.info(
            "Your application has been submitted. Our team will review it within 1–2 business days.",
          );
        } else if (status.status === "Approved") {
          toast.success(
            status.canSelectPriceTier && !status.priceTier?.trim()
              ? "Verified. Choose your monthly payment option next."
              : "Details saved. You can continue with membership.",
          );
        }
        onAdvanceAfterVerification(status);
      } catch (err) {
        const apiMsg = axios.isAxiosError(err)
          ? typeof err.response?.data === "string"
            ? err.response.data
            : (err.response?.data as { title?: string })?.title
          : undefined;
        toast.error(
          apiMsg || "Could not submit your application. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      existingIdOnFile,
      idDocumentFile,
      objectId,
      userData?.user,
      onAdvanceAfterVerification,
      onStatusUpdated,
      queryClient,
      setError,
      prefillFromPersonalInfo,
    ],
  );

  const openDatePicker = useCallback(() => {
    const input = dateInputRef.current;
    if (!input) return;
    input.focus();
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
      } catch {
        // showPicker can throw outside a direct user gesture in some browsers
      }
    }
  }, []);

  const handleIdFileChange = (file: File | undefined) => {
    if (!file) {
      setIdDocumentFile(null);
      return;
    }
    if (!VALID_ID_TYPES.includes(file.type)) {
      setIdUploadInvalid(true);
      setIdDocumentFile(null);
      return;
    }
    if (file.size > MAX_ID_FILE_SIZE) {
      setIdUploadInvalid(true);
      setIdDocumentFile(null);
      return;
    }
    clearErrors("dateOfBirth");
    setIdUploadInvalid(false);
    setIdDocumentFile(file);
  };

  const idUploadMissing = idUploadInvalid && !idDocumentFile && !existingIdOnFile;

  if (userLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-muted">
        <Spinner animation="border" size="sm" role="status" />
        Loading…
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="w-100 d-flex flex-column gap-3">
      <header className="text-center mb-1">
        <h4 className="fw-bold mb-2 fs-5 fs-md-4">
          Verify Young Professional Membership
        </h4>
        <p className="text-muted mb-0 small">
          Complete verification to activate your discounted membership rate.
        </p>
      </header>

      <div className="d-flex align-items-start gap-2 p-3 rounded border border-primary border-opacity-25 bg-primary bg-opacity-10">
        <i className="bi bi-info-circle-fill text-primary flex-shrink-0 mt-1" aria-hidden />
        <div>
          <div className="fw-bold text-primary small">Important</div>
          <p className="mb-0 small text-primary-emphasis">
            Approval is required before the discounted membership rate of{" "}
            <strong>£2/month</strong> can be activated.
          </p>
        </div>
      </div>

      <Accordion
        alwaysOpen
        defaultActiveKey={["requirements", "outcome", "after-submit"]}
        className="d-flex flex-column gap-2"
        style={
          {
            "--bs-accordion-active-bg": "#fff",
            "--bs-accordion-active-color": "var(--bs-body-color)",
            "--bs-accordion-btn-focus-box-shadow": "none",
          } as CSSProperties
        }
      >
        <Accordion.Item eventKey="requirements" className="border rounded overflow-hidden bg-white">
          <Accordion.Header>
            <span className="d-inline-flex align-items-center gap-2 fw-semibold small">
              <i className="bi bi-check-circle-fill text-success" aria-hidden />
              Verification Requirements
            </span>
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
              <RequirementItem>Must be aged 18–23</RequirementItem>
              <RequirementItem>
                Upload a valid government-issued photo ID
              </RequirementItem>
              <RequirementItem>Admin review required</RequirementItem>
            </ul>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="outcome" className="border rounded overflow-hidden bg-white">
          <Accordion.Header>
            <span className="d-inline-flex align-items-center gap-2 fw-semibold small">
              <i className="bi bi-star-fill text-success" aria-hidden />
              Review Outcome
            </span>
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <Row className="g-2 g-md-3">
              <Col xs={6} className="min-w-0">
                <div className="h-100 p-2 p-md-3 rounded border border-success border-opacity-25 bg-success bg-opacity-10">
                  <div className="d-inline-flex align-items-center gap-1 small fw-semibold text-success mb-1">
                    <i className="bi bi-check-circle-fill" aria-hidden />
                    If approved
                  </div>
                  <div className="fw-semibold mb-1 small">
                    Young Professional Membership
                  </div>
                  <div className="fw-bold text-success lh-1 fs-4 fs-md-2">
                    <span className="fs-6 fs-md-5 align-text-top">£</span>
                    2
                    <span className="fs-6 fw-medium">/month</span>
                  </div>
                </div>
              </Col>
              <Col xs={6} className="min-w-0">
                <div className="h-100 p-2 p-md-3 rounded border bg-light">
                  <div className="d-inline-flex align-items-center gap-1 small fw-semibold text-secondary mb-1">
                    <i className="bi bi-x-circle-fill" aria-hidden />
                    If not approved
                  </div>
                  <div className="fw-semibold mb-1 small">
                    Standard Membership
                  </div>
                  <div className="fw-bold text-secondary lh-1 fs-4 fs-md-2">
                    <span className="fs-6 fs-md-5 align-text-top">£</span>
                    10
                    <span className="fs-6 fw-medium">/month</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="after-submit" className="border rounded overflow-hidden bg-white">
          <Accordion.Header>
            <span className="d-inline-flex align-items-center gap-2 fw-semibold small">
              <i className="bi bi-arrow-repeat text-primary" aria-hidden />
              What happens after submission?
            </span>
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <ul className="small text-secondary mb-0 ps-3">
              <li className="mb-2">Your account status will change to Pending Review</li>
              <li className="mb-2">Our team will review your ID within 1–2 business days</li>
              <li className="mb-2">You will not have access to member services during review</li>
              <li className="mb-0">
                You will receive an email once your application is reviewed
              </li>
            </ul>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Card className="border rounded">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex align-items-center gap-2 mb-3 mb-md-4">
            <span
              className="rounded-circle bg-primary bg-opacity-10 text-primary d-inline-flex align-items-center justify-content-center flex-shrink-0 p-2"
              aria-hidden
            >
              <i className="bi bi-person-fill" />
            </span>
            <h5 className="mb-0 fw-semibold">Your Details</h5>
          </div>

          <Form.Group controlId="earlyStageDateOfBirth" className="mb-4">
            <Form.Label className="fw-semibold">
              Date of Birth <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup
              className="cursor-pointer"
              onClick={openDatePicker}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openDatePicker();
                }
              }}
              role="presentation"
            >
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    ref={(el: HTMLInputElement | null) => {
                      field.ref(el);
                      dateInputRef.current = el;
                    }}
                    type="date"
                    className="cursor-pointer"
                    isInvalid={!!errors.dateOfBirth}
                    autoComplete="bday"
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      clearErrors("dateOfBirth");
                    }}
                  />
                )}
              />
              <InputGroup.Text className="bg-white">
                <i className="bi bi-calendar3 text-secondary" aria-hidden />
              </InputGroup.Text>
            </InputGroup>
            {errors.dateOfBirth ? (
              <div className="invalid-feedback d-block">
                {errors.dateOfBirth.message}
              </div>
            ) : (
              <Form.Text className="text-muted">
                You must be between 18 and 23 years old.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className={`fw-semibold ${idUploadMissing ? "text-danger" : ""}`}>
              Upload Valid ID <span className="text-danger">*</span>
            </Form.Label>
            <input
              ref={idFileInputRef}
              type="file"
              className="d-none"
              accept=".png,.jpg,.jpeg,.pdf,.heif,.heic"
              onChange={(e) =>
                handleIdFileChange((e.target as HTMLInputElement).files?.[0])
              }
            />
            <div
              className={`d-flex flex-column align-items-center justify-content-center text-center border border-2 border-dashed rounded p-3 p-md-4 py-4 bg-light ${
                idUploadMissing
                  ? "border-danger bg-danger bg-opacity-10"
                  : "border-secondary-subtle"
              }`}
              role="button"
              tabIndex={0}
              onClick={() => idFileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  idFileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleIdFileChange(e.dataTransfer.files?.[0]);
              }}
            >
              <i className="bi bi-cloud-arrow-up text-secondary fs-2 mb-2" />
              {idDocumentFile ? (
                <span className="small text-success fw-medium">
                  {idDocumentFile.name}
                </span>
              ) : existingIdOnFile ? (
                <span className="small text-success">ID already on file</span>
              ) : (
                <>
                  <span className="d-block fw-medium small">
                    Tap to upload or drag and drop
                  </span>
                  <span className="text-muted small">
                    PNG, JPG, PDF — max 5MB
                  </span>
                </>
              )}
            </div>
            <Form.Text className="text-muted">
              Accepted ID: Passport, Driving Licence, National ID card
            </Form.Text>
          </Form.Group>

          <Controller
            name="youngProfessionalConsent"
            control={control}
            render={({ field }) => (
              <Form.Check
                type="checkbox"
                id="youngProfessionalConsent"
                className="mb-0"
                label={
                  <span className="small">
                    I understand approval is required before the{" "}
                    <strong>discounted membership rate</strong> can be activated.
                  </span>
                }
                checked={field.value === 1}
                onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                isInvalid={!!errors.youngProfessionalConsent}
                feedback={errors.youngProfessionalConsent?.message}
                feedbackType="invalid"
              />
            )}
          />
        </Card.Body>
      </Card>

      <Button
        type="submit"
        className="w-100 py-2 fw-semibold"
        disabled={submitting}
      >
        {submitting ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" role="status" />
            Completing verification…
          </>
        ) : (
          <>
            <i className="bi bi-lock-fill me-2" aria-hidden />
            Complete Verification
          </>
        )}
      </Button>

      <div className="d-flex flex-nowrap align-items-center justify-content-between gap-2 py-3 px-3 bg-light border-top small text-muted mx-n3 mb-n3 mx-md-0 mb-md-0 rounded-bottom">
        <span className="d-inline-flex align-items-start gap-1 flex-grow-1 min-w-0 lh-sm">
          <i className="bi bi-shield-check text-success flex-shrink-0" aria-hidden />
          Your information is secure and encrypted.
        </span>
        <a href="/" className="text-primary text-decoration-none fw-medium flex-shrink-0">
          Privacy Policy
        </a>
      </div>
    </Form>
  );
}

export default memo(RegistrationEarlyStageAgeStepInner);
